// Merkan · Oberflächensprache (Deutsch oder Englisch, je Benutzer und Gerät)
//
// Grundsatz: Der deutsche Text im Code ist der Schlüssel. t('Neuer Vorgang') liefert im Englischen den Eintrag
// aus den Wörterbüchern unter src/i18n/en/*.ts, sonst den deutschen Text (nichts geht verloren).
// Platzhalter in geschweiften Klammern: t('{n} Vorgänge', { n: 3 }).
// Die Sprache wird beim Start gelesen. Ein Wechsel lädt die Seite neu, damit auch Konstanten auf Modulebene
// (Schrittnamen, Kategorien, Zahl- und Datumsformate) in der neuen Sprache entstehen.

export type Sprache = 'de' | 'en';
const SCHLUESSEL = 'merkan.sprache';

function startSprache(): Sprache {
  try {
    const s = localStorage.getItem(SCHLUESSEL);
    if (s === 'de' || s === 'en') return s;
  } catch { /* ohne Speicher Deutsch */ }
  return 'de';
}

const aktuell: Sprache = startSprache();

const module = import.meta.glob<{ default: Record<string, string> }>('../i18n/en/*.ts', { eager: true });
const woerterbuch: Record<string, string> = Object.assign({}, ...Object.values(module).map((m) => m.default));

const fehlend = new Set<string>();

/** Name der KI in der Oberfläche (branchenunabhängig). Texte schreiben {ki}, t() setzt den Namen ein. Fachliche Stellen (Einstellungen, Kontingent, Rechte) sagen weiter „KI“. */
export const KI_NAME = 'Hugo';

/** Übersetzt einen deutschen Oberflächentext in die aktuelle Sprache und setzt Platzhalter ein */
export function t(de: string, werte?: Record<string, string | number | null | undefined>): string {
  let s = de;
  if (aktuell === 'en') {
    const en = woerterbuch[de];
    if (en != null) s = en;
    else if (import.meta.env.DEV && de.trim() && !fehlend.has(de)) { fehlend.add(de); console.debug('[sprache] fehlt:', de); }
  }
  if (s.includes('{ki}')) s = s.replaceAll('{ki}', werte?.ki != null ? String(werte.ki) : KI_NAME);
  return werte ? s.replace(/\{(\w+)\}/g, (m, k) => (werte[k] != null ? String(werte[k]) : m)) : s;
}

/** Einzahl oder Mehrzahl: tn(n, '{n} Vorgang', '{n} Vorgänge') */
export function tn(n: number, einzahl: string, mehrzahl: string, werte?: Record<string, string | number | null | undefined>): string {
  return t(n === 1 ? einzahl : mehrzahl, { n, ...werte });
}

export const sprache = (): Sprache => aktuell;
/** Gebietsschema für Intl (Zahlen, Datum). Englisch in britischer Schreibweise mit Tag vor Monat. */
export const gebiet = (): string => (aktuell === 'en' ? 'en-GB' : 'de-DE');

/** Wechsel durch den Benutzer: im eigenen Profil speichern (gilt auf allen Geräten), dann neu laden */
export async function spracheSetzen(s: Sprache) {
  if (s === aktuell) return;
  try { localStorage.setItem(SCHLUESSEL, s); } catch { /* egal */ }
  try {
    const { supabase } = await import('./supabase');
    const { data } = await supabase.auth.getUser();
    if (data.user) await supabase.from('profile').update({ sprache: s }).eq('id', data.user.id);
  } catch { /* Gerät merkt sich die Sprache trotzdem */ }
  window.location.reload();
}

/** Nach der Anmeldung: gespeicherte Sprache des Benutzers übernehmen (anderes Gerät, anderer Browser) */
export function spracheAusProfil(s: string | null | undefined) {
  if ((s !== 'de' && s !== 'en') || s === aktuell) return;
  try { localStorage.setItem(SCHLUESSEL, s); } catch { return; }
  window.location.reload();
}

if (typeof document !== 'undefined') document.documentElement.lang = aktuell;

/** Für Tests im Prüfstand: fehlende Einträge der aktuellen Seite */
export const fehlendeUebersetzungen = () => [...fehlend];
